import { z } from 'zod';
import { parseMentions } from './mention';
import type { LabelColour } from './label';
import { ulidSchema } from './id';
import { cardTitleSchema } from './card';
import { COMMENT_BODY_MAX_LENGTH } from './comment';

export const EMAIL_TO_BOARD_MAX_BYTES = 10 * 1024 * 1024;

/**
 * `BE-73.6` (`FR-AUTO-031` AC2): per-attachment and per-message caps for
 * inbound email attachment ingestion. The 10 MB whole-message cap above
 * bounds the mail, not the fan-out: without per-file bounds one 9 MB part
 * or fifty 200 KB parts each become an S3 PUT plus a row inside the
 * request's blast radius. A single part therefore may not exceed 5 MB
 * decoded (half the envelope, so one part cannot starve the body path), a
 * message carries at most 10 ingestible parts, and total decoded attachment
 * bytes stay inside the 10 MB envelope. Over-cap parts are dropped -- the
 * comment still lands -- never failing the whole ingestion.
 */
export const INBOUND_EMAIL_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
export const INBOUND_EMAIL_ATTACHMENT_MAX_COUNT = 10;

/**
 * Schema-level bound on one part's base64 payload in characters. Deliberately
 * looser than the 5 MB decoded policy cap above (5 MB decodes from ~7M
 * characters): the schema bounds parser memory, the service enforces policy
 * by dropping over-cap parts with an accepted response rather than failing
 * the webhook.
 */
export const INBOUND_EMAIL_ATTACHMENT_CONTENT_MAX_LENGTH = 8_000_000;

export const emailToBoardCapturePositionSchema = z.enum(['top', 'bottom']);
export type EmailToBoardCapturePosition = z.infer<
  typeof emailToBoardCapturePositionSchema
>;

export const emailToBoardAddressViewSchema = z.strictObject({
  boardId: ulidSchema,
  userId: ulidSchema,
  address: z.string().email(),
  active: z.boolean(),
  captureListId: ulidSchema.nullable(),
  capturePosition: emailToBoardCapturePositionSchema.nullable(),
});
export type EmailToBoardAddressView = z.infer<typeof emailToBoardAddressViewSchema>;

export const emailCaptureSettingsPatchSchema = z.strictObject({
  listId: ulidSchema,
  position: emailToBoardCapturePositionSchema,
});

export type EmailCaptureSettingsPatch = z.infer<typeof emailCaptureSettingsPatchSchema>;

const inboundEmailAttachmentSchema = z.strictObject({
  name: z.string().trim().min(1).max(255),
  contentType: z.string().trim().min(1).max(255).optional(),
  size: z.number().int().nonnegative(),
  /**
   * `BE-73.6`: the part's bytes as base64, when the provider pushes them in
   * the webhook body. Absent for metadata-only parts, which are dropped
   * (comment still lands) -- never fabricated into a row. This is the bytes
   * variant of the contract: there is deliberately no provider-side URL
   * variant, so the server never fetches an attacker-influenced URL and the
   * SSRF decision is discharged by construction (no allowlist to drift).
   */
  content: z.string().max(INBOUND_EMAIL_ATTACHMENT_CONTENT_MAX_LENGTH).optional(),
});

export type InboundEmailAttachment = z.infer<typeof inboundEmailAttachmentSchema>;

export const inboundEmailBodySchema = z.strictObject({
  messageId: z.string().trim().min(1).max(255),
  threadId: z.string().trim().min(1).max(255).optional(),
  from: z.string().email().max(320),
  to: z.array(z.string().email().max(320)).min(1),
  cc: z.array(z.string().email().max(320)).default([]),
  subject: cardTitleSchema,
  textBody: z.string().max(COMMENT_BODY_MAX_LENGTH).default(''),
  htmlBody: z.string().max(COMMENT_BODY_MAX_LENGTH).optional(),
  messageSize: z.number().int().nonnegative(),
  attachments: z.array(inboundEmailAttachmentSchema).default([]),
});

export type InboundEmailBody = z.infer<typeof inboundEmailBodySchema>;

export const inboundEmailResultSchema = z.strictObject({
  accepted: z.boolean(),
  kind: z.enum(['card', 'comment', 'ignored', 'oversize', 'destination_rejected']),
  cardId: ulidSchema.nullable(),
  commentId: ulidSchema.nullable(),
});

export type InboundEmailResult = z.infer<typeof inboundEmailResultSchema>;

export interface EmailToBoardLabelCandidate {
  readonly id: string;
  readonly name: string | null;
  readonly colour: LabelColour;
}

export interface EmailToBoardMemberCandidate {
  readonly userId: string;
  readonly username: string;
}

export interface EmailToBoardSubjectParse {
  readonly title: string;
  readonly labelIds: readonly string[];
  readonly memberUserIds: readonly string[];
}

const LABEL_TOKEN = /#[A-Za-z0-9_-]+/g;
const WHITESPACE = /\s+/g;
const TOKEN_PRECEDERS = new Set([
  ' ',
  '\t',
  '\n',
  '\r',
  '\f',
  '\v',
  '(',
  '[',
  '{',
  '<',
  '"',
  "'",
  '`',
]);

function normaliseLookup(value: string): string {
  return value.trim().replace(/[_-]+/g, ' ').replace(WHITESPACE, ' ').toLowerCase();
}

function normaliseColour(value: string): string {
  return value.trim().replace(/-/g, '_').toLowerCase();
}

function appendUnique(target: string[], value: string): void {
  if (!target.includes(value)) target.push(value);
}

function labelForToken(
  rawToken: string,
  labels: readonly EmailToBoardLabelCandidate[],
): EmailToBoardLabelCandidate | null {
  const token = rawToken.slice(1);
  const labelNumber = Number.parseInt(token, 10);

  if (/^[1-9][0-9]*$/.test(token)) {
    return labels[labelNumber - 1] ?? null;
  }

  const name = normaliseLookup(token);
  const byName = labels.find((label) => {
    if (label.name === null) return false;
    return normaliseLookup(label.name) === name;
  });
  if (byName) return byName;

  const colour = normaliseColour(token);
  return labels.find((label) => normaliseColour(label.colour) === colour) ?? null;
}

function mentionedUsernamesByUserId(
  subject: string,
  members: readonly EmailToBoardMemberCandidate[],
): Map<string, string> {
  const parsed = parseMentions(subject);
  const byUsername = new Map(
    members.map((member) => [member.username.toLowerCase(), member] as const),
  );
  const result = new Map<string, string>();

  for (const username of parsed) {
    const member = byUsername.get(username.toLowerCase());
    if (member) result.set(member.userId, username);
  }

  return result;
}

function removeMatchedMemberTokens(
  subject: string,
  matchedUsernames: Iterable<string>,
): string {
  let title = subject;

  for (const username of matchedUsernames) {
    const escaped = username.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    title = title.replace(
      new RegExp(
        `(^|[ \\t\\r\\n\\f\\v()[\\]{}<"'\\\`])@${escaped}(?=$|[^A-Za-z0-9._-])`,
        'gi',
      ),
      '$1',
    );
  }

  return title;
}

function compactTitle(value: string): string {
  return value.replace(WHITESPACE, ' ').trim();
}

export function parseEmailToBoardSubject(args: {
  readonly subject: string;
  readonly labels: readonly EmailToBoardLabelCandidate[];
  readonly members: readonly EmailToBoardMemberCandidate[];
}): EmailToBoardSubjectParse {
  const labelIds: string[] = [];
  const matchedLabelSpans: { start: number; end: number }[] = [];

  for (const match of args.subject.matchAll(LABEL_TOKEN)) {
    if (match.index === undefined) continue;
    const preceding = match.index === 0 ? null : (args.subject[match.index - 1] ?? null);
    if (preceding !== null && !TOKEN_PRECEDERS.has(preceding)) continue;

    const label = labelForToken(match[0], args.labels);
    if (!label) continue;

    appendUnique(labelIds, label.id);
    matchedLabelSpans.push({ start: match.index, end: match.index + match[0].length });
  }

  let title = '';
  let cursor = 0;
  for (const span of matchedLabelSpans) {
    title += args.subject.slice(cursor, span.start);
    cursor = span.end;
  }
  title += args.subject.slice(cursor);

  const matchedMembers = mentionedUsernamesByUserId(args.subject, args.members);
  const memberUserIds = [...matchedMembers.keys()];
  title = removeMatchedMemberTokens(title, matchedMembers.values());

  return {
    title: compactTitle(title) || '(no subject)',
    labelIds,
    memberUserIds,
  };
}

export function normalizeEmailAddress(value: string): string {
  const trimmed = value.trim();
  const angleMatch = /<([^<>]+)>/.exec(trimmed);
  const address = angleMatch?.[1] ?? trimmed;
  return address.trim().toLowerCase();
}

const QUOTE_BOUNDARY_PATTERNS = [
  /^-{2,}\s*original message\s*-{2,}$/i,
  /^_{2,}$/,
  /^on .+ wrote:$/i,
  /^from:\s.+$/i,
  /^sent:\s.+$/i,
  /^to:\s.+$/i,
  /^subject:\s.+$/i,
];

function isQuotedLine(line: string): boolean {
  return line.trimStart().startsWith('>');
}

function isQuoteBoundary(line: string): boolean {
  const trimmed = line.trim();
  if (trimmed.length === 0) return false;
  return QUOTE_BOUNDARY_PATTERNS.some((pattern) => pattern.test(trimmed));
}

export function normalizeInboundEmailTextBody(body: string): string {
  const lines = body.replace(/\r\n?/g, '\n').split('\n');
  const kept: string[] = [];

  for (const line of lines) {
    if (isQuoteBoundary(line)) break;
    if (isQuotedLine(line)) continue;
    kept.push(line);
  }

  return kept.join('\n').trim();
}
